import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { Button, useLanguage } from '@appic/ui-components'
import type { AgentSignal } from '@appic/hooks'
import { getPermissionState, requestPermission, type NotificationPermissionState } from '../../lib/pushNotifications'

interface NotificationsPanelProps {
  signals: AgentSignal[]
  onNavigate?: () => void
}

export function NotificationsPanel({ signals, onNavigate }: NotificationsPanelProps) {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [permission, setPermission] = useState<NotificationPermissionState>(() => getPermissionState())

  const onEnable = useCallback(async () => {
    const next = await requestPermission()
    setPermission(next)
  }, [])

  const handleNotificationClick = useCallback((signal: AgentSignal) => {
    const targetRoute = resolveNotificationTargetRoute(signal)
    if (!targetRoute) {
      console.warn('[NotificationsPanel] missing target route for signal', { id: signal.id, type: signal.signal_type, kind: signal.kind })
      return
    }
    navigate(targetRoute)
    onNavigate?.()
  }, [navigate, onNavigate])

  return (
    <div className="flex flex-col max-h-96">
      {permission === 'default' && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-muted/30">
          <span className="text-xs">{t('header.notifications.enablePrompt')}</span>
          <Button size="sm" variant="default" className="h-7 text-xs" type="button" onClick={() => void onEnable()}>
            {t('header.notifications.enableCta')}
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <span className="text-sm font-medium">{t('header.notifications.title')}</span>
      </div>
      <div className="overflow-y-auto overscroll-contain">
        {signals.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
            <Bell className="h-6 w-6 opacity-40" />
            <span className="text-xs">{t('header.notifications.empty')}</span>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {signals.slice(0, 20).map((s) => {
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => handleNotificationClick(s)}
                    className="flex w-full min-h-11 min-w-11 flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-muted/50"
                  >
                    <span className="text-xs font-medium">{s.source !== null ? s.source : 'unknown'}</span>
                    <span className="text-xs text-muted-foreground line-clamp-2">
                      {s.payload?.text ?? JSON.stringify(s.payload)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(s.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return null
}

export function resolveNotificationTargetRoute(signal: AgentSignal): string | null {
  const payload = signal.payload ?? {}
  const explicitTarget = asString(payload.target_route)
  if (explicitTarget) return explicitTarget

  const kind = asString(payload.kind) ?? signal.kind ?? signal.signal_type
  const subjectId = asString(payload.subject_id)

  if (kind === 'pr_review_requested') {
    const prNumber = asNumber(payload.pr_number) ?? asNumber(subjectId)
    return prNumber != null ? `/atc/bridge/prs/${prNumber}` : null
  }
  if (kind === 'decision_pending') {
    const decisionId = asString(payload.decision_id) ?? subjectId
    return decisionId ? `/atc/decisions/${encodeURIComponent(decisionId)}` : null
  }
  if (kind === 'agent_message') {
    const agentId = asString(payload.agent_id) ?? asString(signal.source)
    const sessionId = asString(payload.session_id)
    if (!agentId) return null
    return sessionId
      ? `/atc/bridge/agents/chat/${encodeURIComponent(agentId)}?session=${encodeURIComponent(sessionId)}`
      : `/atc/bridge/agents/chat/${encodeURIComponent(agentId)}`
  }

  const room = asString(signal.source)
  return room ? `/atc/bridge/agents/chat?room=${encodeURIComponent(room)}` : null
}
